import { inject, Injectable } from "@angular/core";
import { Meta } from "@angular/platform-browser";
import { BoardConfigApi, User } from "@front/m19-api-client";
import { ILayoutService } from "@front/m19-ui";
import { environment } from "../environments/environment";

type Agency = {
  subDomain: string;
  name: string;
  smallLogo: string;
  logo: string;
  termsAndConditionsLink: string;
  isSelfService: boolean;
  contact?: string;
  upgrade?: string;
  upgradeTarget?: LinkTarget;
  thirdPartyScriptLoader?: () => void;
  redirect?: string;
  referralProgram?: string;
};

export enum BoardType {
  M19 = "M19",
  WHITELABEL = "WHITELABEL",
  WHITELABEL_SELFSERVICE = "WHITELABEL_SELFSERVICE",
}

export type Link = {
  url: string;
  target: "_self" | "_blank";
};

export type LinkTarget = "_self" | "_blank";

declare global {
  interface Window {
    intercomSettings?: {
      api_base: string;
      app_id: string;
    };
  }
}

@Injectable()
export class LayoutSelectorService implements ILayoutService {
  private readonly NO_SUB_DOMAIN = "";
  private readonly M19 = "m19";
  private boardType?: BoardType = undefined;
  private readonly boardConfigApi = inject(BoardConfigApi);

  // Don't forget to update the column board of configdb.m19Users table when adding/updating an agency
  private readonly _agencies: Array<Agency> = [
    {
      subDomain: this.NO_SUB_DOMAIN,
      name: "",
      smallLogo: "blank_40.png",
      logo: "blank_40.png",
      termsAndConditionsLink: "",
      isSelfService: false,
    },
    {
      subDomain: this.M19,
      name: "M19",
      smallLogo: "logo.svg",
      logo: "logo_header.svg",
      termsAndConditionsLink: "https://www.m19.com/terms-and-conditions",
      isSelfService: false,
      referralProgram: "https://www.m19.com/get-rewards-for-your-account",
    },
    {
      subDomain: "ppclogic",
      name: "PPC Logic",
      smallLogo: "ppclogic_small.png",
      logo: "ppclogic.png",
      termsAndConditionsLink: "https://ppclogic.com/terms-of-service/",
      isSelfService: true,
      upgrade: "https://customers.ppclogic.com/register.php",
      upgradeTarget: "_self",
    },
    {
      subDomain: "etail-agency",
      name: "eTail Agency",
      smallLogo: "etail-agency_small.png",
      logo: "etail-agency.png",
      termsAndConditionsLink: "https://www.etail-agency.com",
      isSelfService: false,
    },
    {
      subDomain: "signalytics",
      name: "Signalytics",
      smallLogo: "signalytics_small.png",
      logo: "signalytics.png",
      termsAndConditionsLink: "https://signalytics.ai/terms-and-conditions-ppc/",
      isSelfService: true,
      contact: "https://www.signalytics.ai/contact/",
      upgrade: "https://signalytics.ai/pricing/",
    },
    {
      subDomain: "m2asolutions",
      name: "M2A Solutions",
      smallLogo: "m2a_small.png",
      logo: "m2a.svg",
      termsAndConditionsLink: "https://m2asolutions.com/",
      isSelfService: false,
    },
    {
      subDomain: "sellingz",
      name: "Sellingz",
      smallLogo: "sellingz_small.png",
      logo: "sellingz.png",
      termsAndConditionsLink: "https://sellingz.com/",
      isSelfService: false,
    },
    {
      subDomain: "southparc",
      name: "Southparc",
      smallLogo: "logo.svg",
      logo: "logo_header.svg",
      termsAndConditionsLink: "https://southparc.nl/fr/algemene_voorwaarden_southparc/",
      isSelfService: true,
      contact: "https://southparc.nl/m19/m19-contact/",
      upgrade: "https://southparc.nl/m19/",
    },
    {
      subDomain: "upcounting",
      name: "Upcounting",
      smallLogo: "upcounting_small.png",
      logo: "upcounting.png",
      termsAndConditionsLink: "https://upcounting.com/terms-and-conditions/",
      isSelfService: true,
      contact: "https://calendly.com/nik-bakhshalian/30min",
      upgrade: "https://calendly.com/nik-bakhshalian/30min",
    },
    {
      subDomain: "amzppc",
      name: "AMZPPC",
      smallLogo: "amzppc_small.png",
      logo: "amzppc.png",
      termsAndConditionsLink: "https://www.amazonppc.co.uk/software-terms-and-conditions/",
      isSelfService: true,
      contact: "https://www.amazonppc.co.uk/contact",
      upgrade: "https://www.amazonppc.co.uk/contact",
    },
    {
      subDomain: "astra-sellrbox",
      name: "Astra",
      smallLogo: "sellrbox.png",
      logo: "sellrbox_logo.png",
      termsAndConditionsLink: "https://astra.sellrbox.com/terms-of-service",
      isSelfService: true,
      contact: "https://intercom.help/astra-by-sellrbox/en/",
      upgrade: "https://astra.sellrbox.com/pricing",
      thirdPartyScriptLoader: () => {
        window.intercomSettings = {
          api_base: "https://api-iam.intercom.io",
          app_id: "m9bib9bf",
        };
        const script = document.createElement("script");
        script.innerText = `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/m9bib9bf';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
        `;
        const head = document.getElementsByTagName("head")[0];
        head.appendChild(script);
        const userGuidingScript = document.createElement("script");
        userGuidingScript.innerText = `(function(g,u,i,d,e,s){g[e]=g[e]||[];var f=u.getElementsByTagName(i)[0];var k=u.createElement(i);k.async=true;k.src='https://static.userguiding.com/media/user-guiding-'+s+'-embedded.js';f.parentNode.insertBefore(k,f);if(g[d])return;var ug=g[d]={q:[]};ug.c=function(n){return function(){ug.q.push([n,arguments])};};var m=['previewGuide','finishPreview','track','identify', 'hideChecklist','launchChecklist'];for(var j=0;j<m.length;j+=1){ug[m[j]]=ug.c(m[j]);}})(window,document,'script','userGuiding','userGuidingLayer','ZNQ100256567ID');`;
        head.appendChild(userGuidingScript);
      },
      referralProgram: "https://astra.sellrbox.com/referral",
    },
  ];

  readonly agencies: Map<string, Agency> = this._agencies.reduce(function (map, obj) {
    map.set(obj.subDomain, obj);
    return map;
  }, new Map<string, Agency>());

  readonly agency: Agency;

  public readonly isWhitelabel: boolean;
  private readonly hostname: string;

  constructor() {
    if (environment.whitelabelDomain) {
      this.hostname = environment.whitelabelDomain;
    } else {
      const locationHostname = window.location.hostname;
      if (locationHostname.endsWith("m19.com")) {
        this.hostname = "m19";
      } else if (locationHostname.includes("localhost")) {
        this.hostname = "m19";
      } else {
        this.hostname = locationHostname.split(".")[0];
      }
    }
    this.isWhitelabel = !this.hostname.includes("m19");
    this.agency = this.agencies.get(this.getSubDomain())!;
    if (environment.environment === "local") {
      this.boardConfigApi.getBoardName().subscribe((r) => {
        const boardName = r.boardName;
        if (boardName !== this.hostname) {
          // eslint-disable-next-line no-console
          console.warn(
            `Board name from API (${boardName}) does not match the hostname (${this.hostname}). This may cause issues with the layout.`,
            `Start the backend with the command "mvn jetty:run -Dboard.name=${this.hostname}"`,
          );
        }
      });
    }
  }

  getBoardType(): BoardType {
    if (this.boardType) {
      return this.boardType;
    }

    if (this.isWhitelabel) {
      this.boardType = this.agency.isSelfService ? BoardType.WHITELABEL_SELFSERVICE : BoardType.WHITELABEL;
      return this.boardType;
    }
    this.boardType = BoardType.M19;
    return this.boardType;
  }

  getTermsAndConditionLink(): string {
    return this.agency.termsAndConditionsLink;
  }

  getAgencyName(): string {
    return this.agency.name;
  }

  getSmallIcon(): string {
    return "assets/img/" + this.agency.smallLogo;
  }

  getLogo(): string {
    return "assets/img/" + this.agency.logo;
  }

  getContact(): string | undefined {
    return this.agency.contact;
  }

  getUpgrade(user: User): Link {
    // 'encrypt' user email in base64
    const userEmailQueryParam = user?.email ? `?u=${btoa(user.email)}` : "";
    const agency = this.agency;
    return { url: agency.upgrade + userEmailQueryParam, target: agency.upgradeTarget! };
  }

  getRedirect(): string | undefined {
    const redirect = this.agency.redirect;
    if (!redirect) {
      return undefined;
    }
    return `https://${redirect}.ppc-board.com`;
  }

  getSubDomain(): string {
    if (!this.isWhitelabel) return this.M19;
    if (this.agencies.has(this.hostname)) {
      return this.hostname;
    }
    return this.NO_SUB_DOMAIN;
  }

  getBoardTheme(): string {
    const subDomain = this.getSubDomain();
    if (!this.isWhitelabel || subDomain === this.NO_SUB_DOMAIN) return this.M19 + "-theme";
    return subDomain + "-theme";
  }

  getPageTitle() {
    const subDomain = this.getSubDomain();
    return subDomain.charAt(0).toUpperCase() + subDomain.slice(1) + " Dashboard";
  }

  setDocumentMetadata(document: Document, meta: Meta): void {
    document.querySelector<HTMLLinkElement>("#favicon")!.href = this.getSmallIcon();

    meta.updateTag({ name: "description", content: this.getPageTitle() });
    meta.updateTag({ name: "author", content: this.getSubDomain() });
    meta.updateTag({ name: "keyword", content: `${this.getSubDomain()},Amazon,Advertising,PPC` });
  }

  loadThirdPartyScript(): void {
    const loader = this.agency?.thirdPartyScriptLoader;
    if (loader) {
      loader();
    }
  }

  enableGradientOnAuthLayout(): boolean {
    return !this.isWhitelabel;
  }
}
